import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSurveyRuntimeApi } from "../context/SurveyRuntimeApiContext";
import { useTenant } from "../../contexts/TenantContext";
import { getApiErrorMessage } from "../api/runtimeErrors";
import { useSurveyUiToast } from "../hooks/useSurveyUiToast";
import { SurveyFields } from "../components/SurveyFields";
import { Toast } from "../components/SurveyToast";
import SurveyShareDialog from "../components/SurveyShareDialog";
import { CustomizeQuestionModal } from "../components/CustomizeQuestionModal";
import { CUSTOMIZABLE_TYPES, EMOTION_PRESETS, questionConfigSummary } from "../components/questionConfig";
import { useDebounce } from "../hooks/useDebounce";
import SurveyClientsEditor from "../components/SurveyClientsEditor";
import SurveyBannerEditor from "../components/SurveyBannerEditor";
import QuestionTemplatePicker from "../components/QuestionTemplatePicker";
import AddQuestionGroupPrompt from "../components/AddQuestionGroupPrompt";
import QuestionGroupEditorDialog from "../components/QuestionGroupEditorDialog";
import { isMasterTenant } from "../utils/masterTenant";
import { parseSectionTarget, sectionTarget } from "../utils/branchingPath";
const RESPONDENT_NAME_ID = "jll-respondent-name";
const RESPONDENT_EMAIL_ID = "jll-respondent-email";
function isRespondentProfileQuestion(q) {
  return q.id === RESPONDENT_NAME_ID || q.id === RESPONDENT_EMAIL_ID;
}
function respondentNameTemplate(sectionIndex = null) {
  return {
    id: RESPONDENT_NAME_ID,
    type: "text",
    label: "Full name",
    required: true,
    sort_order: 0,
    options: [],
    config: null,
    section_index: sectionIndex
  };
}
function respondentEmailTemplate(sectionIndex = null) {
  return {
    id: RESPONDENT_EMAIL_ID,
    type: "text",
    label: "Email address",
    required: true,
    sort_order: 1,
    options: [],
    config: { variant: "email" },
    section_index: sectionIndex
  };
}
function normalizeRespondentBlockOrder(qs) {
  const nameIdx = qs.findIndex((q) => q.id === RESPONDENT_NAME_ID);
  const emailIdx = qs.findIndex((q) => q.id === RESPONDENT_EMAIL_ID);
  if (nameIdx === -1 || emailIdx === -1) return qs.map((x, i) => ({ ...x, sort_order: i }));
  const nameQ = qs[nameIdx];
  const emailQ = qs[emailIdx];
  const rest = qs.filter((q) => q.id !== RESPONDENT_NAME_ID && q.id !== RESPONDENT_EMAIL_ID);
  return [nameQ, emailQ, ...rest].map((x, i) => ({ ...x, sort_order: i }));
}
function stripRespondentProfileQuestions(qs) {
  return qs.filter((q) => !isRespondentProfileQuestion(q)).map((x, i) => ({ ...x, sort_order: i }));
}
function ensureRespondentProfileShape(q) {
  if (q.id === RESPONDENT_NAME_ID) {
    const merged = {
      ...q.config && typeof q.config === "object" ? q.config : {},
      profile: "respondent_name"
    };
    return { ...q, type: "text", options: [], config: stableConfig(merged) };
  }
  if (q.id === RESPONDENT_EMAIL_ID) {
    const merged = {
      ...q.config && typeof q.config === "object" ? q.config : {},
      variant: "email",
      profile: "respondent_email"
    };
    return { ...q, type: "text", options: [], config: stableConfig(merged) };
  }
  return q;
}
function prependRespondentProfileQuestions(qs, defaultSectionIndex = null) {
  const existingName = qs.find((q) => q.id === RESPONDENT_NAME_ID);
  const existingEmail = qs.find((q) => q.id === RESPONDENT_EMAIL_ID);
  const rest = stripRespondentProfileQuestions(qs);
  const nameQ = existingName ? ensureRespondentProfileShape(existingName) : respondentNameTemplate(defaultSectionIndex);
  const emailQ = existingEmail ? ensureRespondentProfileShape(existingEmail) : respondentEmailTemplate(defaultSectionIndex);
  return [nameQ, emailQ, ...rest].map((x, i) => ({ ...x, sort_order: i }));
}
const TYPE_LABELS = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Paragraph" },
  { value: "rating", label: "Rating" },
  { value: "single", label: "Single choice" },
  { value: "multi", label: "Multiselect" },
  { value: "emotion", label: "Sentiment scale" },
  { value: "likert", label: "Likert scale" }
];
function needsOptions(t) {
  return t === "single" || t === "multi" || t === "emotion" || t === "likert";
}
function defaultOptions(t) {
  if (t === "emotion") {
    return [
      { key: "very_negative", label: "Very negative" },
      { key: "negative", label: "Negative" },
      { key: "neutral", label: "Neutral" },
      { key: "positive", label: "Positive" },
      { key: "very_positive", label: "Very positive" }
    ];
  }
  if (t === "likert") {
    // Likert options are the matrix's statement rows; the scale lives in config.
    return [
      { key: "stmt_a", label: "Statement 1" },
      { key: "stmt_b", label: "Statement 2" }
    ];
  }
  return [
    { key: "a", label: "Option A" },
    { key: "b", label: "Option B" }
  ];
}
function newQuestion(sort, sourceGroupId = null, sectionIndex = null) {
  return {
    id: crypto.randomUUID(),
    type: "text",
    label: "",
    required: false,
    sort_order: sort,
    options: [],
    config: null,
    // Question bank identity: null = brand-new question (created in the bank
    // on save); source_group_id also adds it to that group.
    question_definition_id: null,
    source_group_id: sourceGroupId,
    // Per-survey branching for single-choice questions:
    // { rules: { [optionKey]: targetQuestionId | "__END__" }, default: ... }
    branching: null,
    // Index into the survey's `sections` list; null = no section (flat survey
    // or intentionally unassigned — shows in a catch-all "More" tab).
    section_index: sectionIndex
  };
}
function stableConfig(cfg) {
  if (!cfg || typeof cfg !== "object") return null;
  const keys = Object.keys(cfg).sort();
  if (keys.length === 0) return null;
  const out = {};
  for (const k of keys) out[k] = cfg[k];
  return out;
}
const BRANCH_END = "__END__";
// Legacy rows may carry a single string target / "__END__" / "default" from
// the old skip-logic model; coerce to the list shape and drop the sentinels.
function coerceBranchTargets(value) {
  if (typeof value === "string") return value === BRANCH_END ? [] : [value];
  if (Array.isArray(value)) return value.filter((t) => typeof t === "string" && t !== BRANCH_END);
  return [];
}
// Rewrite "sec:<n>" targets across every question's rules when sections are
// reordered/removed. mapFn(secIdx) returns the new index, or null to drop.
function remapSectionTargets(qs, mapFn) {
  return qs.map((q) => {
    if (!q.branching?.rules) return q;
    let sawSection = false;
    const rules = {};
    for (const [k, v] of Object.entries(q.branching.rules)) {
      const targets = [];
      for (const t of coerceBranchTargets(v)) {
        const secIdx = parseSectionTarget(t);
        if (secIdx === null) {
          targets.push(t);
          continue;
        }
        sawSection = true;
        const mapped = mapFn(secIdx);
        if (mapped != null) targets.push(sectionTarget(mapped));
      }
      if (targets.length) rules[k] = targets;
    }
    if (!sawSection) return q;
    return { ...q, branching: Object.keys(rules).length ? { rules } : null };
  });
}
function stableBranching(b) {
  if (!b || typeof b !== "object") return null;
  const rules = b.rules && typeof b.rules === "object" ? b.rules : null;
  if (!rules) return null;
  const out = {};
  for (const k of Object.keys(rules).sort()) {
    const targets = coerceBranchTargets(rules[k]);
    if (targets.length) out[k] = [...targets].sort();
  }
  return Object.keys(out).length ? { rules: out } : null;
}
function branchRuleCount(q) {
  if (!q.branching) return 0;
  return Object.values(q.branching.rules ?? {}).filter((v) => coerceBranchTargets(v).length).length;
}
// Display-condition self-heal: only single-choice questions carry rules,
// rules must go through an option that still exists, and revealed questions
// must be LATER questions (forward-only — no cycles). Anything invalidated by
// a reorder, removal, or type change is silently dropped. The server
// normalizes again on save as a backstop.
function sanitizeBranching(qs, sectionCount = 0) {
  return qs.map((q, i) => {
    if (!q.branching) return q;
    if (q.type !== "single") return { ...q, branching: null };
    const laterIds = new Set(qs.slice(i + 1).map((x) => x.id));
    const optKeys = new Set(q.options.map((o) => o.key));
    // Section targets stay only while forward-only: the question must sit in
    // a section and may only reveal a LATER section that still exists.
    const secTargetOk = (secIdx) =>
      Number.isInteger(q.section_index) && secIdx > q.section_index && secIdx < sectionCount;
    const rules = {};
    for (const [k, v] of Object.entries(q.branching.rules ?? {})) {
      if (!optKeys.has(k)) continue;
      const targets = coerceBranchTargets(v).filter((t) => {
        const secIdx = parseSectionTarget(t);
        return secIdx === null ? laterIds.has(t) : secTargetOk(secIdx);
      });
      if (targets.length) rules[k] = targets;
    }
    return { ...q, branching: Object.keys(rules).length ? { rules } : null };
  });
}
// Closed surveys are read-only (clone-only) — mirrors the manage list's status logic.
function isDetailClosed(d) {
  if (d.is_recurrence_parent) return d.live_window_state === "closed";
  return d.live_window_state === "closed" || d.status === "closed";
}
function packDraftFingerprint(t, desc, showNumbers, sections, qs) {
  const trimmed = t.trim();
  return JSON.stringify({
    title: trimmed,
    description: (desc || "").trim(),
    show_question_numbers: !!showNumbers,
    sections: sections.map((s) => ({ tab_label: s.tab_label, title: s.title || null })),
    questions: qs.map((q, i) => ({
      id: q.id,
      type: q.type,
      label: q.label,
      required: q.required,
      sort_order: i,
      options: needsOptions(q.type) ? q.options.map((o) => ({ key: o.key, label: o.label, description: o.description || null, is_other: !!o.is_other })) : [],
      config: stableConfig(q.config),
      branching: stableBranching(q.branching),
      section_index: q.section_index ?? null
    }))
  });
}
function formatUtcPreview(value) {
  if (!value) return "\u2014";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const iso = d.toISOString().slice(0, 16).replace("T", " ");
  return `${iso} UTC`;
}
function isoToDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}
function defaultCloseDatetimeLocal(opensLocal) {
  const base = opensLocal ? new Date(opensLocal) : /* @__PURE__ */ new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + 7);
  base.setMinutes(base.getMinutes() - base.getTimezoneOffset());
  return base.toISOString().slice(0, 16);
}
function liveWindowLabel(state) {
  if (state === "open") return "Accepting responses";
  if (state === "not_open") return "Scheduled (not yet open)";
  if (state === "closed") return "Closed";
  return "";
}
function formatLocalDateTime(iso) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString();
}
function formatFrequencyLabel(freq) {
  if (!freq) return "";
  return freq.charAt(0).toUpperCase() + freq.slice(1);
}
function localTimeToUtcHHMM(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  const d = /* @__PURE__ */ new Date();
  d.setHours(h, m, 0, 0);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}
function utcTimeToLocalLabel(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  const d = /* @__PURE__ */ new Date();
  d.setUTCHours(h, m, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function parseEmailList(raw) {
  return raw.split(",").map((e) => e.trim()).filter(Boolean);
}
function SurveyBuilderPage() {
  const { apiFetch } = useSurveyRuntimeApi();
  const { currentUser, currentTenant } = useTenant();
  const showClientUi = isMasterTenant(currentTenant);
  const { id } = useParams();
  const { showError } = useSurveyUiToast();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [showQuestionNumbers, setShowQuestionNumbers] = useState(false);
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPosition, setBannerPosition] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  // Optional wizard/tab pages. Each entry is { tab_label, title }; a question
  // joins one via its section_index (position in this array), or null for
  // "no section" (flat survey, or intentionally unassigned).
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [status, setStatus] = useState("");
  const [activeToken, setActiveToken] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveWindowState, setLiveWindowState] = useState(null);
  const [scheduleOpensAt, setScheduleOpensAt] = useState("");
  const [scheduleClosesAt, setScheduleClosesAt] = useState("");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [recurrence, setRecurrence] = useState(null);
  const [recurrenceInstanceCount, setRecurrenceInstanceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDraftToast, setShowDraftToast] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showPublishToast, setShowPublishToast] = useState(false);
  const [copyToastKey, setCopyToastKey] = useState(0);
  const [err, setErr] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishSuccessOpen, setPublishSuccessOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmEndLiveOpen, setConfirmEndLiveOpen] = useState(false);
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyToEmail, setNotifyToEmail] = useState("");
  const [notifyCcEmailsRaw, setNotifyCcEmailsRaw] = useState("");
  const notifyToPrefilled = useRef(false);
  const [publishPreviewSurvey, setPublishPreviewSurvey] = useState(null);
  const [publishPreviewLoading, setPublishPreviewLoading] = useState(false);
  const [publishPreviewErr, setPublishPreviewErr] = useState(null);
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [publishMode, setPublishMode] = useState("once");
  const [recurrenceFrequency, setRecurrenceFrequency] = useState("monthly");
  const [windowDurationDays, setWindowDurationDays] = useState(7);
  const [dailyOpenTime, setDailyOpenTime] = useState("09:00");
  const [dailyCloseTime, setDailyCloseTime] = useState("17:00");
  const [seriesEndsAt, setSeriesEndsAt] = useState("");
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  const [customizeIdx, setCustomizeIdx] = useState(null);
  // Question ids whose branching panel is expanded (ids survive reorders).
  const [branchingOpen, setBranchingOpen] = useState(/* @__PURE__ */ new Set());
  const [duplicateTitle, setDuplicateTitle] = useState(false);
  const [savedDraftFingerprint, setSavedDraftFingerprint] = useState(null);
  const [labelErrors, setLabelErrors] = useState(/* @__PURE__ */ new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  // Drag-to-reorder: armedIdx makes a card draggable only while the mouse is
  // down on its grip, so text selection inside the card's inputs still works.
  const [armedIdx, setArmedIdx] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragScrollRef = useRef(null);
  useEffect(() => {
    if (armedIdx == null) return;
    const disarm = () => setArmedIdx(null);
    window.addEventListener("mouseup", disarm);
    return () => window.removeEventListener("mouseup", disarm);
  }, [armedIdx]);
  useEffect(() => {
    if (dragIdx == null) {
      if (dragScrollRef.current) {
        window.cancelAnimationFrame(dragScrollRef.current);
        dragScrollRef.current = null;
      }
      return;
    }
    const EDGE = 120;
    const MAX_STEP = 18;
    let pointerY = window.innerHeight / 2;
    const tick = () => {
      const viewportHeight = window.innerHeight;
      let delta = 0;
      if (pointerY < EDGE) {
        delta = -Math.ceil(((EDGE - pointerY) / EDGE) * MAX_STEP);
      } else if (pointerY > viewportHeight - EDGE) {
        delta = Math.ceil(((pointerY - (viewportHeight - EDGE)) / EDGE) * MAX_STEP);
      }
      if (delta !== 0) {
        window.scrollBy(0, delta);
      }
      dragScrollRef.current = window.requestAnimationFrame(tick);
    };
    const handleDragOverWindow = (e) => {
      pointerY = e.clientY;
    };
    window.addEventListener("dragover", handleDragOverWindow);
    dragScrollRef.current = window.requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("dragover", handleDragOverWindow);
      if (dragScrollRef.current) {
        window.cancelAnimationFrame(dragScrollRef.current);
        dragScrollRef.current = null;
      }
    };
  }, [dragIdx]);
  const [clients, setClients] = useState([]);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [groupPromptOpen, setGroupPromptOpen] = useState(false);
  // Create a question group straight from the builder (same dialog as the Groups page).
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [canCurateGroups, setCanCurateGroups] = useState(false);
  const [templatePickerKey, setTemplatePickerKey] = useState(0);
  const isSaving = useRef(false);
  const initialLoadDone = useRef(false);
  const savedSnapshot = useRef(null);
  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setSavedDraftFingerprint(null);
    setErr(null);
    try {
      const d = await apiFetch(`/admin/surveys/${id}`);
      if (isDetailClosed(d)) {
        showError("This survey is closed and can no longer be edited. Clone it to make changes.");
        navigate("/survey", { replace: true });
        return;
      }
      const nextTitle = d.draft.title || d.title;
      const nextDescription = d.description ?? d.draft.description ?? "";
      const nextShowQuestionNumbers = !!(d.show_question_numbers ?? d.draft.show_question_numbers);
      const nextSections = (d.draft.sections || []).map((s) => ({
        tab_label: s.tab_label || "",
        title: s.title || ""
      }));
      const qs = d.draft.questions || [];
      const nextQuestions = qs.map((q, i) => {
        const rawType = String(q.type || "text").toLowerCase();
        const mappedType = rawType === "dropdown" ? "single" : rawType === "text" || rawType === "textarea" || rawType === "rating" || rawType === "single" || rawType === "multi" || rawType === "emotion" || rawType === "likert" ? rawType : "text";
        // Saved drafts come back with question_keys as ids; the profile
        // marker restores the stable ids the respondent-details logic
        // (pinning, checkbox state, locked type) keys off.
        const profile = q.config && typeof q.config === "object" ? q.config.profile : null;
        const stableId = profile === "respondent_name" ? RESPONDENT_NAME_ID : profile === "respondent_email" ? RESPONDENT_EMAIL_ID : null;
        return {
          id: stableId || q.id || crypto.randomUUID(),
          type: mappedType,
          label: q.label || "",
          required: !!q.required,
          sort_order: q.sort_order ?? i,
          options: (q.options || []).map((o) => ({
            key: o.key || o.option_key || "opt",
            label: o.label || "",
            description: o.description || "",
            is_other: !!o.is_other
          })),
          config: stableConfig(q.config),
          // Bank identity: preserved so edits become copy-on-write versions
          // (and group questions update their group) instead of new questions.
          question_definition_id: q.question_definition_id ?? null,
          source_group_id: q.source_group_id ?? null,
          branching: q.branching ?? null,
          section_index: typeof q.section_index === "number" ? q.section_index : null
        };
      });
      const ordered = sanitizeBranching(
        normalizeRespondentBlockOrder(nextQuestions).map(ensureRespondentProfileShape),
        nextSections.length
      );
      setTitle(nextTitle);
      setDescription(nextDescription);
      setShowQuestionNumbers(nextShowQuestionNumbers);
      setBannerImage(d.banner_image ?? null);
      setBannerPosition(d.banner_position ?? null);
      setLogoImage(d.logo_image ?? null);
      setSections(nextSections);
      setQuestions(ordered);
      setSavedDraftFingerprint(packDraftFingerprint(nextTitle, nextDescription, nextShowQuestionNumbers, nextSections, ordered));
      setStatus(d.status);
      setActiveToken(d.active_public_token ?? null);
      setLiveOpen(d.is_live_window_open);
      setLiveWindowState(d.live_window_state ?? null);
      setScheduleOpensAt(isoToDatetimeLocal(d.opens_at_utc));
      setScheduleClosesAt(isoToDatetimeLocal(d.closes_at_utc));
      setRecurrence(d.recurrence ?? null);
      setRecurrenceInstanceCount(d.recurrence_instance_count ?? 0);
      setClients(d.clients ?? []);
      if (d.notify_to_email) {
        setNotifyToEmail(d.notify_to_email);
        notifyToPrefilled.current = true;
      }
      if (d.notify_cc_emails && d.notify_cc_emails.length) {
        setNotifyCcEmailsRaw(d.notify_cc_emails.join(", "));
      }
      savedSnapshot.current = {
        title: d.draft.title || d.title,
        questions: d.draft.questions || []
      };
    } catch (e) {
      const message = getApiErrorMessage(e, "Failed to load the survey.");
      setErr(message);
      showError(message);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [id, showError, navigate]);
  useEffect(() => {
    void load();
  }, [load]);
  // Whether this user may curate the global groups/bank (gates "New group").
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await apiFetch("/admin/question-bank/can-curate");
        if (!cancelled) setCanCurateGroups(Boolean(r?.can_curate));
      } catch {
        if (!cancelled) setCanCurateGroups(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch]);
  useEffect(() => {
    if (!publishOpen || !id) return;
    setPreviewAnswers({});
    setPublishPreviewSurvey(null);
    setPublishPreviewErr(null);
    let cancelled = false;
    setPublishPreviewLoading(true);
    void (async () => {
      try {
        const data = await apiFetch(`/admin/surveys/${id}/preview`);
        if (!cancelled) setPublishPreviewSurvey(data);
      } catch (e) {
        if (!cancelled) {
          const message = getApiErrorMessage(e, "Preview failed to load.");
          setPublishPreviewErr(message);
          showError(message);
        }
      } finally {
        if (!cancelled) setPublishPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, publishOpen, savedDraftFingerprint, showError]);
  const draftSnapshot = useMemo(
    () => ({ title, description, showQuestionNumbers, sections, questions }),
    [title, description, showQuestionNumbers, sections, questions]
  );
  const debouncedDraft = useDebounce(draftSnapshot, 3e3);
  function isValidForAutoSave(t, qs) {
    return t.trim().length > 0 && qs.length > 0 && // at least one question
    qs.every((q) => q.label.trim().length > 0);
  }
  const questionsReadyForPublish = useMemo(
    () => questions.length > 0 && questions.every((q) => q.label.trim().length > 0),
    [questions]
  );
  const publishBlockedReason = useMemo(() => {
    if (!title.trim()) return "Enter a survey title before publishing.";
    if (questions.length === 0) return "Add at least one question before you can publish this survey.";
    if (!questionsReadyForPublish) return "Enter question text for every question before publishing.";
    return null;
  }, [title, questions, questionsReadyForPublish]);
  const draftNeedsSave = useMemo(() => {
    if (savedDraftFingerprint === null) return true;
    return (
      packDraftFingerprint(title, description, showQuestionNumbers, sections, questions) !== savedDraftFingerprint
    );
  }, [savedDraftFingerprint, title, description, showQuestionNumbers, sections, questions]);
  const isRecurrenceParent = Boolean(recurrence);
  const publishDisabled =
    publishBlockedReason !== null ||
    draftNeedsSave ||
    isRecurrenceParent;
  const publishButtonTitle = useMemo(() => {
    if (isRecurrenceParent) {
      return "This survey is a recurring parent template. Cycles are published automatically.";
    }
    if (draftNeedsSave) return "Save your draft before publishing.";
    if (publishBlockedReason) return publishBlockedReason;
    return void 0;
  }, [draftNeedsSave, publishBlockedReason, isRecurrenceParent]);
  const collectRespondentInfo = useMemo(
    () => questions.length >= 2 && questions[0].id === RESPONDENT_NAME_ID && questions[1].id === RESPONDENT_EMAIL_ID,
    [questions]
  );
  function setCollectRespondentInfo(enabled) {
    if (enabled) {
      const defaultSectionIndex = sections.length > 0 ? 0 : null;
      setQuestions((qs) => {
        if (qs.length >= 2 && qs[0].id === RESPONDENT_NAME_ID && qs[1].id === RESPONDENT_EMAIL_ID) {
          return qs;
        }
        return prependRespondentProfileQuestions(qs, defaultSectionIndex);
      });
    } else {
      setQuestions((qs) => stripRespondentProfileQuestions(qs));
    }
  }
  // Respondent name/email must always render first — first in the question
  // list (handled by normalizeRespondentBlockOrder, applied wherever
  // questions are reordered), and first in whichever section is CURRENTLY
  // first (section_index 0), regardless of when sections were added or how
  // they've since been reordered/removed. Re-pins on every relevant change
  // rather than only at toggle-on time, since sections.length===0 at
  // toggle-on time is a common case (sections often get added afterward),
  // and moveSection's generic remap doesn't know these two are special.
  useEffect(() => {
    if (!collectRespondentInfo) return;
    const targetSectionIndex = sections.length > 0 ? 0 : null;
    setQuestions((qs) => {
      let changed = false;
      const next = qs.map((q) => {
        if (isRespondentProfileQuestion(q) && q.section_index !== targetSectionIndex) {
          changed = true;
          return { ...q, section_index: targetSectionIndex };
        }
        return q;
      });
      return changed ? next : qs;
    });
  }, [sections, collectRespondentInfo]);
  useEffect(() => {
    if (publishOpen && publishDisabled) setPublishOpen(false);
  }, [publishOpen, publishDisabled]);
  useEffect(() => {
    if (!publishOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [publishOpen]);
  useEffect(() => {
    if (!publishOpen || notifyToEmail || !currentUser?.email) return;
    setNotifyToEmail(currentUser.email);
  }, [publishOpen, notifyToEmail, currentUser]);
  const notifyCcEmails = useMemo(() => parseEmailList(notifyCcEmailsRaw), [notifyCcEmailsRaw]);
  const notifyToValid = EMAIL_RE.test(notifyToEmail.trim());
  const notifyCcValid = notifyCcEmails.length === 0 || notifyCcEmails.every((e) => EMAIL_RE.test(e));
  // Notify fields only gate publishing when the checkbox is ticked.
  const notifyValid = !notifyEnabled || (notifyToValid && notifyCcValid);
  useEffect(() => {
    if (!publishSuccessOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [publishSuccessOpen]);
  useEffect(() => {
    if (!confirmEndLiveOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [confirmEndLiveOpen]);
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (!id) return;
    if (!isValidForAutoSave(debouncedDraft.title, debouncedDraft.questions)) return;
    void saveDraft(true);
  }, [debouncedDraft]);
  async function saveDraft(silent = false) {
    if (!id) return;
    // Shared lock: the manual "Save draft" click and the autosave debounce both
    // call this function. Without it, a manual save fired while an autosave is
    // still in flight sends two overlapping PUT /draft requests for the same
    // survey — the loser's insert then collides with rows the winner already
    // committed (ux_sq_version_def duplicate key).
    if (isSaving.current) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      if (!silent) showError("Survey title is missing.");
      return;
    }
    isSaving.current = true;
    setSavingDraft(true);
    setShowDraftToast(false);
    setLabelErrors(/* @__PURE__ */ new Set());
    try {
      await apiFetch(`/admin/surveys/${id}/draft`, {
        method: "PUT",
        body: JSON.stringify({
          title: trimmedTitle,
          description: description.trim() || null,
          show_question_numbers: showQuestionNumbers,
          // Sent as a plain positional array — questions reference a section by
          // its index here (section_index), so this must NOT be filtered or
          // reordered relative to that. The backend skips any entry with a
          // blank tab_label on its own, without shifting the other indices.
          sections: sections.map((s) => ({ tab_label: s.tab_label.trim(), title: s.title.trim() || null })),
          questions: questions.map((q, i) => ({
            id: q.id,
            type: q.type,
            label: q.label,
            required: q.required,
            sort_order: i,
            options: needsOptions(q.type) ? q.options.map((o) => ({ key: o.key, label: o.label, description: o.description || null, is_other: !!o.is_other })) : [],
            config: stableConfig(q.config),
            question_definition_id: q.question_definition_id ?? null,
            source_group_id: q.source_group_id ?? null,
            branching: q.type === "single" ? stableBranching(q.branching) : null,
            section_index: q.section_index ?? null
          }))
        })
      });
      if (silent) {
        setSavedDraftFingerprint(
          packDraftFingerprint(trimmedTitle, description, showQuestionNumbers, sections, questions)
        );
      }
      setShowDraftToast(true);
      setLabelErrors(/* @__PURE__ */ new Set());
      if (!silent) void load();
    } catch (e) {
      const message = getApiErrorMessage(e, "Save failed.");
      if (!silent) {
        showError(message);
      }
    } finally {
      isSaving.current = false;
      setSavingDraft(false);
    }
  }
  async function saveSchedule(e) {
    e.preventDefault();
    if (!id || !scheduleOpensAt || !scheduleClosesAt) return;
    const opens = new Date(scheduleOpensAt);
    const closes = new Date(scheduleClosesAt);
    if (closes <= opens) {
      showError("Close time must be after open time.");
      return;
    }
    setScheduleSaving(true);
    try {
      const r = await apiFetch(`/admin/surveys/${id}/schedule`, {
        method: "PATCH",
        body: JSON.stringify({
          opens_at_utc: opens.toISOString(),
          closes_at_utc: closes.toISOString()
        })
      });
      setLiveOpen(r.is_live_window_open);
      setLiveWindowState(r.live_window_state);
      setScheduleOpensAt(isoToDatetimeLocal(r.opens_at_utc));
      setScheduleClosesAt(isoToDatetimeLocal(r.closes_at_utc));
      void load();
    } catch (e2) {
      showError(getApiErrorMessage(e2, "Failed to update schedule."));
    } finally {
      setScheduleSaving(false);
    }
  }
  async function submitPublish(e) {
    e.preventDefault();
    if (!id || !opensAt) return;
    if (publishDisabled) {
      if (!title.trim()) showError("Survey title is missing.");
      else if (draftNeedsSave) showError("Save your draft before publishing.");
      else if (publishBlockedReason) showError(publishBlockedReason);
      return;
    }
    if (!notifyValid) {
      showError("Enter a valid notification email (and valid CC addresses) or untick Notify recipients.");
      return;
    }
    if (publishMode === "recurring") {
      const isDaily = recurrenceFrequency === "daily";
      if (isDaily) {
        if (!dailyOpenTime || !dailyCloseTime) return;
        if (dailyOpenTime === dailyCloseTime) {
          showError("Daily open and close times must be different.");
          return;
        }
      } else if (!windowDurationDays) return;
      if (seriesEndsAt && new Date(seriesEndsAt) <= new Date(opensAt)) {
        showError("Series end must be after the series start.");
        return;
      }
      if (!window.confirm(
        "Set up a recurring schedule? The first cycle will be spawned automatically."
      )) {
        return;
      }
      setPublishing(true);
      try {
        await apiFetch(`/admin/surveys/${id}/recurrence`, {
          method: "POST",
          body: JSON.stringify({
            frequency: recurrenceFrequency,
            window_duration_days: isDaily ? 1 : windowDurationDays,
            daily_open_time: isDaily ? localTimeToUtcHHMM(dailyOpenTime) : null,
            daily_close_time: isDaily ? localTimeToUtcHHMM(dailyCloseTime) : null,
            exclude_weekends: isDaily ? false : excludeWeekends,
            series_starts_at_utc: new Date(opensAt).toISOString(),
            series_ends_at_utc: seriesEndsAt ? new Date(seriesEndsAt).toISOString() : null,
            spawn_current_period: true,
            notify_to_email: notifyEnabled ? notifyToEmail.trim() : null,
            notify_cc_emails: notifyEnabled ? notifyCcEmails : []
          })
        });
        setPublishOpen(false);
        setShowPublishToast(true);
        setShowDraftToast(false);
        void load();
      } catch (e2) {
        showError(getApiErrorMessage(e2, "Failed to set up recurring schedule."));
      } finally {
        setPublishing(false);
      }
      return;
    }
    if (!closesAt.trim()) return;
    const opens = new Date(opensAt);
    const closes = new Date(closesAt);
    if (closes <= opens) {
      showError("Close time must be after open time.");
      return;
    }
    if (!window.confirm(
      "Publish this survey now? A new published version and public link will be created with the schedule you set."
    )) {
      return;
    }
    setPublishing(true);
    try {
      const r = await apiFetch(`/admin/surveys/${id}/publish`, {
        method: "POST",
        body: JSON.stringify({
          opens_at_utc: opens.toISOString(),
          closes_at_utc: closes.toISOString(),
          notify_to_email: notifyEnabled ? notifyToEmail.trim() : null,
          notify_cc_emails: notifyEnabled ? notifyCcEmails : []
        })
      });
      setPublishOpen(false);
      setActiveToken(r.public_token);
      setStatus("published");
      setLiveOpen(true);
      setPublishSuccessOpen(true);
      setShowPublishToast(true);
      setShowDraftToast(false);
      void load();
    } catch (e2) {
      showError(getApiErrorMessage(e2, "Publish failed."));
    } finally {
      setPublishing(false);
    }
  }
  async function endLive() {
    if (!id) return;
    try {
      await apiFetch(`/admin/surveys/${id}/end-live`, { method: "POST" });
      setActiveToken(null);
      setLiveOpen(false);
      setStatus("closed");
      setPublishSuccessOpen(false);
      void load();
    } catch (e) {
      showError(getApiErrorMessage(e, "Failed to end the live survey."));
    } finally {
      setConfirmEndLiveOpen(false);
    }
  }
  const usedGroupIds = useMemo(
    () => [...new Set(questions.map((q) => q.source_group_id).filter(Boolean))],
    [questions]
  );
  // New questions default onto the last section, if any — the author is
  // presumably working on that tab. They can reassign via the question's
  // Section dropdown.
  const lastSectionIndex = sections.length > 0 ? sections.length - 1 : null;
  function insertQuestionAtTop(list, q) {
    const pinned = list.filter(isRespondentProfileQuestion);
    const rest = list.filter((x) => !isRespondentProfileQuestion(x));
    return [...pinned, q, ...rest].map((x, i) => ({ ...x, sort_order: i }));
  }
  function insertQuestionAt(list, index, q) {
    const copy = [...list];
    const pinnedCount = copy.filter(isRespondentProfileQuestion).length;
    const safeIndex = Math.max(pinnedCount, Math.min(index, copy.length));
    copy.splice(safeIndex, 0, q);
    return copy.map((x, i) => ({ ...x, sort_order: i }));
  }
  function addQuestion() {
    // When the survey uses question groups, ask whether the new custom question
    // should also join one of them (it always joins the bank). Only curators of
    // the global groups get the prompt — clients can't change group contents.
    if (canCurateGroups && usedGroupIds.length > 0) {
      setGroupPromptOpen(true);
      return;
    }
    setQuestions((q) => insertQuestionAtTop(q, newQuestion(0, null, lastSectionIndex)));
  }
  function addQuestionBelow(idx) {
    setQuestions((q) => insertQuestionAt(q, idx + 1, newQuestion(0, null, lastSectionIndex)));
  }
  function addQuestionWithGroup(sourceGroupId) {
    setGroupPromptOpen(false);
    setQuestions((q) => insertQuestionAtTop(q, newQuestion(0, sourceGroupId, lastSectionIndex)));
  }
  function addSection() {
    setSections((s) => [...s, { tab_label: `Section ${s.length + 1}`, title: "" }]);
  }
  function patchSection(idx, patch) {
    setSections((s) => {
      const copy = [...s];
      copy[idx] = { ...copy[idx], ...patch };
      return copy;
    });
  }
  function moveSection(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    setSections((s) => {
      const copy = [...s];
      const t = copy[idx];
      copy[idx] = copy[j];
      copy[j] = t;
      return copy;
    });
    setQuestions((qs) => {
      const moved = qs.map((q) => {
        if (q.section_index === idx) return { ...q, section_index: j };
        if (q.section_index === j) return { ...q, section_index: idx };
        return q;
      });
      // Keep "sec:<n>" display-logic targets pointing at the same section,
      // then drop any that stopped being forward-only after the swap.
      const remapped = remapSectionTargets(moved, (s) => (s === idx ? j : s === j ? idx : s));
      return sanitizeBranching(remapped, sections.length);
    });
  }
  function removeSection(idx) {
    setSections((s) => s.filter((_, i) => i !== idx));
    setQuestions((qs) => {
      const shifted = qs.map((q) => {
        if (q.section_index == null) return q;
        if (q.section_index === idx) return { ...q, section_index: null };
        if (q.section_index > idx) return { ...q, section_index: q.section_index - 1 };
        return q;
      });
      const remapped = remapSectionTargets(shifted, (s) => (s === idx ? null : s > idx ? s - 1 : s));
      return sanitizeBranching(remapped, sections.length - 1);
    });
  }
  function setQuestionSection(idx, rawValue) {
    const sectionIndex = rawValue === "" ? null : Number(rawValue);
    setQuestions((qs) => {
      const copy = [...qs];
      copy[idx] = { ...copy[idx], section_index: Number.isInteger(sectionIndex) ? sectionIndex : null };
      // Moving a question between sections can invalidate its own section
      // targets (they must point at a LATER section than its new home).
      return sanitizeBranching(copy, sections.length);
    });
  }
  function applyTemplateGroups(groups) {
    setTemplatePickerOpen(false);
    setQuestions((prev) => {
      const present = new Set(prev.map((q) => q.question_definition_id).filter(Boolean));
      const additions = [];
      for (const g of groups) {
        for (const qd of g.questions || []) {
          if (present.has(qd.id)) continue;
          present.add(qd.id);
          const rawType = String(qd.type || "text").toLowerCase();
          const mappedType = rawType === "dropdown" ? "single" : ["text", "textarea", "rating", "single", "multi", "emotion", "likert"].includes(rawType) ? rawType : "text";
          additions.push({
            id: crypto.randomUUID(),
            type: mappedType,
            label: qd.label || "",
            required: false,
            sort_order: prev.length + additions.length,
            options: (qd.options || []).map((o) => ({ key: o.option_key, label: o.label, description: o.description || "", is_other: !!o.is_other })),
            config: stableConfig(qd.config),
            question_definition_id: qd.id,
            source_group_id: g.id,
            branching: null,
            section_index: lastSectionIndex
          });
        }
      }
      return [...prev, ...additions];
    });
  }
  function removeQuestion(idx) {
    setConfirmRemove(null);
    setQuestions((q) => sanitizeBranching(q.filter((_, i) => i !== idx).map((x, i) => ({ ...x, sort_order: i })), sections.length));
  }
  function requestRemove(idx) {
    const q = questions[idx];
    const hasContent = q.label.trim() !== "" || q.options.length > 0;
    if (hasContent) {
      setConfirmRemove(idx);
    } else {
      removeQuestion(idx);
    }
  }
  function move(idx, dir) {
    setQuestions((q) => {
      const j = idx + dir;
      if (j < 0 || j >= q.length) return q;
      const copy = [...q];
      const t = copy[idx];
      copy[idx] = copy[j];
      copy[j] = t;
      const reordered = copy.map((x, i) => ({ ...x, sort_order: i }));
      return sanitizeBranching(normalizeRespondentBlockOrder(reordered).map(ensureRespondentProfileShape), sections.length);
    });
  }
  function reorderQuestion(from, to) {
    setQuestions((q) => {
      if (from == null || to == null || from === to || from < 0 || to < 0 || from >= q.length || to >= q.length) return q;
      const copy = [...q];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      const reordered = copy.map((x, i) => ({ ...x, sort_order: i }));
      return sanitizeBranching(normalizeRespondentBlockOrder(reordered).map(ensureRespondentProfileShape), sections.length);
    });
  }
  function endDrag() {
    setDragIdx(null);
    setDragOverIdx(null);
    setArmedIdx(null);
  }
  function patchQuestion(idx, patch) {
    setQuestions((q) => {
      const copy = [...q];
      const cur = { ...copy[idx], ...patch };
      if (patch.type && needsOptions(patch.type) && cur.options.length === 0) {
        cur.options = defaultOptions(patch.type);
      }
      if (patch.type && !needsOptions(patch.type)) {
        cur.options = [];
      }
      if (patch.type && patch.type !== copy[idx].type) {
        cur.config = null;
      }
      if (patch.type && patch.type !== "single") {
        cur.branching = null;
      }
      copy[idx] = cur;
      return sanitizeBranching(copy, sections.length);
    });
  }
  function setQuestionConfig(idx, next) {
    setQuestions((q) => {
      const copy = [...q];
      let cfg = stableConfig(next);
      if (copy[idx]?.id === RESPONDENT_EMAIL_ID) {
        cfg = stableConfig({ ...cfg ?? {}, variant: "email" });
      }
      copy[idx] = { ...copy[idx], config: cfg };
      return copy;
    });
  }
  function setQuestionOptions(idx, opts) {
    setQuestions((q) => {
      const copy = [...q];
      copy[idx] = { ...copy[idx], options: opts };
      return copy;
    });
  }
  function patchOption(qidx, oidx, label) {
    setQuestions((q) => {
      const copy = [...q];
      const opts = [...copy[qidx].options];
      opts[oidx] = { ...opts[oidx], label };
      copy[qidx] = { ...copy[qidx], options: opts };
      return copy;
    });
  }
  function patchOptionDescription(qidx, oidx, description) {
    setQuestions((q) => {
      const copy = [...q];
      const opts = [...copy[qidx].options];
      opts[oidx] = { ...opts[oidx], description };
      copy[qidx] = { ...copy[qidx], options: opts };
      return copy;
    });
  }
  // "Other" option: respondents who pick it get an optional text box, and the
  // typed value is stored as "other:<text>" in place of the option key. One
  // per question — the stored form doesn't say which option it came from.
  function patchOptionIsOther(qidx, oidx, isOther) {
    setQuestions((q) => {
      const copy = [...q];
      const opts = copy[qidx].options.map((o, i) => ({ ...o, is_other: i === oidx ? isOther : false }));
      copy[qidx] = { ...copy[qidx], options: opts };
      return copy;
    });
  }
  function addOption(qidx) {
    setQuestions((q) => {
      const copy = [...q];
      const n = copy[qidx].options.length;
      copy[qidx] = {
        ...copy[qidx],
          options: [...copy[qidx].options, { key: `opt_${n + 1}`, label: `Option ${n + 1}`, is_other: false }]

      };
      return copy;
    });
  }
  function removeOption(qidx, oidx) {
    setQuestions((q) => {
      const copy = [...q];
      copy[qidx] = {
        ...copy[qidx],
        options: copy[qidx].options.filter((_, i) => i !== oidx)
      };
      return sanitizeBranching(copy, sections.length);
    });
  }
  function toggleBranchingOpen(qid) {
    setBranchingOpen((s) => {
      const next = new Set(s);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  }
  // Per-option "show these questions" picker: selecting the option reveals
  // the checked LATER questions (forward-only, so no loops). Questions not
  // named in any rule always stay in the survey.
  function toggleOptionBranchTarget(qidx, optKey, targetId) {
    setQuestions((qs) => {
      const copy = [...qs];
      const q = copy[qidx];
      const cur = coerceBranchTargets(q.branching?.rules?.[optKey]);
      const next = cur.includes(targetId) ? cur.filter((t) => t !== targetId) : [...cur, targetId];
      const rules = { ...(q.branching?.rules ?? {}) };
      if (next.length) rules[optKey] = next;
      else delete rules[optKey];
      copy[qidx] = { ...q, branching: Object.keys(rules).length ? { rules } : null };
      return copy;
    });
  }
  function branchShowPanel(qidx, opt) {
    const q = questions[qidx];
    const later = questions.slice(qidx + 1);
    const selected = new Set(coerceBranchTargets(q.branching?.rules?.[opt.key]));
    return /* @__PURE__ */ jsxs("div", { className: "builder-branch-show", children: [
      /* @__PURE__ */ jsx("span", { className: "builder-branch-show-title", children: `If "${opt.label || "this option"}" is selected, also show:` }),
      later.length === 0 ? /* @__PURE__ */ jsx("span", { className: "muted small builder-branch-show-empty", children: "No later questions yet — add questions below this one first." }) : /* @__PURE__ */ jsx("div", { className: "builder-branch-show-list", children: later.map((t, ti) => /* @__PURE__ */ jsxs("label", { className: "builder-branch-show-option", children: [
        /* @__PURE__ */ jsx("input", {
          type: "checkbox",
          checked: selected.has(t.id),
          onChange: () => toggleOptionBranchTarget(qidx, opt.key, t.id)
        }),
        /* @__PURE__ */ jsx("span", { title: t.label || "Untitled question", children: `Q${qidx + 2 + ti} — ${(t.label || "Untitled question").slice(0, 60)}` })
      ] }, t.id)) }),
      // Whole later sections can be revealed too — their tab stays hidden
      // until this option is chosen. Only offered when the question itself
      // sits in a section (forward-only, like question targets).
      Number.isInteger(q.section_index) && sections.some((s, si) => si > q.section_index && s.tab_label.trim()) && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("span", { className: "builder-branch-show-title builder-branch-show-subtitle", children: "…and open these sections:" }),
        /* @__PURE__ */ jsx("div", { className: "builder-branch-show-list", children: sections.map((s, si) => si > q.section_index && s.tab_label.trim() ? /* @__PURE__ */ jsxs("label", { className: "builder-branch-show-option", children: [
          /* @__PURE__ */ jsx("input", {
            type: "checkbox",
            checked: selected.has(sectionTarget(si)),
            onChange: () => toggleOptionBranchTarget(qidx, opt.key, sectionTarget(si))
          }),
          /* @__PURE__ */ jsx("span", { title: s.tab_label, children: `Section ${si + 1} — ${s.tab_label.slice(0, 60)}` })
        ] }, `sec-${si}`) : null) })
      ] })
    ] });
  }
  if (loading) {
    return /* @__PURE__ */ jsx(Box, { sx: { textAlign: "center", py: 8 }, children: /* @__PURE__ */ jsx(CircularProgress, {}) });
  }
  if (err && !title && questions.length === 0) {
    return /* @__PURE__ */ jsxs("div", { className: "page", children: [
      /* @__PURE__ */ jsx("p", { className: "muted", children: "Survey details are unavailable right now." }),
      /* @__PURE__ */ jsx(Link, { to: "/survey/manage", children: "Back to list" })
    ] });
  }
  const publicUrl = activeToken ? `${window.location.origin}/survey/p/s/${activeToken}` : "";
  const liveWindowStatusText = isRecurrenceParent
    ? `Recurring \xB7 ${formatFrequencyLabel(recurrence.frequency)}`
    : liveWindowLabel(liveWindowState) ||
      (liveOpen
        ? "Live window open"
        : status === "published"
          ? "Live window not open"
          : status === "closed"
            ? "Live window closed"
            : "");
  const hasOneTimeSchedule = Boolean(activeToken && scheduleOpensAt && scheduleClosesAt);
  const showSchedulePanel = isRecurrenceParent || hasOneTimeSchedule;
  return /* @__PURE__ */ jsxs("div", { className: "page builder-page", children: [
    /* @__PURE__ */ jsxs("header", { className: "page-header", children: [
      /* @__PURE__ */ jsx(Typography, { variant: "h4", component: "h1", sx: { fontWeight: 600 }, children: title.trim() || "Untitled survey" }),
      /* @__PURE__ */ jsxs(Typography, { variant: "body2", color: "text.secondary", children: [
        "Add questions, save a draft, preview, then publish. Status: ",
        /* @__PURE__ */ jsx("strong", { children: status }),
        liveWindowStatusText && ` \xB7 ${liveWindowStatusText}`
      ] }),
      showClientUi && /* @__PURE__ */ jsx(Box, { sx: { mt: 1 }, children: /* @__PURE__ */ jsx(
        SurveyClientsEditor,
        {
          surveyId: id,
          clients,
          onSaved: (next) => setClients(next)
        }
      ) })
    ] }),
    showDraftToast && /* @__PURE__ */ jsx(
      Toast,
      {
        message: "Draft saved.",
        variant: "success",
        duration: 3e3,
        onDismiss: () => setShowDraftToast(false)
      }
    ),
    showPublishToast && /* @__PURE__ */ jsx(
      Toast,
      {
        message: "Survey published. Public link is ready to share.",
        variant: "success",
        duration: 3500,
        onDismiss: () => setShowPublishToast(false)
      }
    ),
    copyToastKey > 0 && /* @__PURE__ */ jsx(
      Toast,
      {
        message: "Link copied to clipboard.",
        variant: "success",
        duration: 2e3,
        onDismiss: () => setCopyToastKey(0)
      },
      copyToastKey
    ),
    /* @__PURE__ */ jsxs("div", { className: "builder-toolbar card", children: [
      /* @__PURE__ */ jsx(Link, { className: "btn", to: "/survey/manage", children: "\u2190 Surveys" }),
      /* @__PURE__ */ jsx("button", { type: "button", className: "btn", disabled: savingDraft, onClick: () => void saveDraft(), children: savingDraft ? "Saving…" : "Save draft" }),
      /* @__PURE__ */ jsx(Link, { className: "btn secondary", to: `/survey/${id}/preview`, children: "Preview" }),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "btn primary",
          disabled: publishDisabled,
          title: publishButtonTitle,
          onClick: () => {
            if (publishDisabled || !id) return;
            setPublishOpen(true);
            setPublishMode("once");
            setNotifyEnabled(false);
            setPublishPreviewErr(null);
            if (!opensAt) {
              const d = /* @__PURE__ */ new Date();
              d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
              const localOpen = d.toISOString().slice(0, 16);
              setOpensAt(localOpen);
              if (!closesAt) setClosesAt(defaultCloseDatetimeLocal(localOpen));
            } else if (!closesAt) {
              setClosesAt(defaultCloseDatetimeLocal(opensAt));
            }
          },
          children: "Publish"
        }
      ),
      liveOpen && activeToken && /* @__PURE__ */ jsx("button", { type: "button", className: "btn danger", onClick: () => setConfirmEndLiveOpen(true), children: "Cancel live survey" })
    ] }),
    showSchedulePanel && /* @__PURE__ */ jsxs("div", { className: "card builder-schedule-card", children: [
      /* @__PURE__ */ jsx("h3", { className: "builder-schedule-title", children: isRecurrenceParent ? "Recurring schedule" : "Response window" }),
      isRecurrenceParent ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("p", { className: "muted small builder-schedule-lede", children: [
          "This survey is the ",
          /* @__PURE__ */ jsx("strong", { children: "parent template" }),
          ". Each cycle creates a separate survey (e.g. ",
          /* @__PURE__ */ jsxs("em", { children: [recurrence.base_title || title, " January"] }),
          ") with its own response window and responses."
        ] }),
        /* @__PURE__ */ jsxs("dl", { className: "builder-schedule-dl", children: [
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("dt", { children: "Repeats" }),
            /* @__PURE__ */ jsx("dd", { children: formatFrequencyLabel(recurrence.frequency) })
          ] }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("dt", { children: recurrence.frequency === "daily" ? "Open daily" : "Open per cycle" }),
            recurrence.frequency === "daily" ? /* @__PURE__ */ jsxs("dd", { children: [utcTimeToLocalLabel(recurrence.daily_open_time), " – ", utcTimeToLocalLabel(recurrence.daily_close_time)] }) : /* @__PURE__ */ jsxs("dd", { children: [recurrence.window_duration_days, " days", recurrence.exclude_weekends ? " (weekdays only)" : ""] })
          ] }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("dt", { children: "Series starts" }),
            /* @__PURE__ */ jsx("dd", { children: formatLocalDateTime(recurrence.series_starts_at_utc) })
          ] }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("dt", { children: "Series ends" }),
            /* @__PURE__ */ jsx("dd", { children: recurrence.series_ends_at_utc ? formatLocalDateTime(recurrence.series_ends_at_utc) : "No end date" })
          ] }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("dt", { children: "Series status" }),
            /* @__PURE__ */ jsx("dd", { children: recurrence.status })
          ] }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("dt", { children: "Cycles spawned" }),
            /* @__PURE__ */ jsx("dd", { children: recurrenceInstanceCount })
          ] })
        ] }),
        /* @__PURE__ */ jsx("p", { className: "muted small", children: "Edit questions here to update the template for future cycles. To change recurrence settings or view cycle links, use Manage surveys." }),
        /* @__PURE__ */ jsx(Link, { className: "btn secondary", to: "/survey/manage", children: "Manage surveys" })
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("p", { className: "muted small builder-schedule-lede", children: [
          "Responses are only accepted between the open and close times. The survey ",
          /* @__PURE__ */ jsx("strong", { children: "closes automatically" }),
          " when the close time passes."
        ] }),
        liveWindowState && /* @__PURE__ */ jsxs("p", { className: "builder-schedule-status", children: [
          "Status: ",
          /* @__PURE__ */ jsx("strong", { children: liveWindowLabel(liveWindowState) || liveWindowState })
        ] }),
        /* @__PURE__ */ jsxs("form", { className: "builder-schedule-form", onSubmit: (e) => void saveSchedule(e), children: [
          /* @__PURE__ */ jsxs("label", { className: "field", children: [
            /* @__PURE__ */ jsx("span", { children: "Opens at (local time)" }),
            /* @__PURE__ */ jsx("input", { type: "datetime-local", required: true, value: scheduleOpensAt, onChange: (e) => setScheduleOpensAt(e.target.value) })
          ] }),
          /* @__PURE__ */ jsxs("label", { className: "field", children: [
            /* @__PURE__ */ jsx("span", { children: "Closes at (local time)" }),
            /* @__PURE__ */ jsx("input", { type: "datetime-local", required: true, value: scheduleClosesAt, onChange: (e) => setScheduleClosesAt(e.target.value) })
          ] }),
          /* @__PURE__ */ jsx("button", { type: "submit", className: "btn secondary", disabled: scheduleSaving, children: scheduleSaving ? "Saving schedule\u2026" : "Save schedule" })
        ] }),
        !liveOpen && liveWindowState === "closed" && /* @__PURE__ */ jsx("p", { className: "muted small", children: "This survey is closed. Publish again to create a new live window." })
      ] })
    ] }),
    hasOneTimeSchedule && status !== "closed" && !isRecurrenceParent && /* @__PURE__ */ jsxs("div", { className: "card builder-link-card", children: [
      /* @__PURE__ */ jsx("div", { className: "builder-link-head", children: /* @__PURE__ */ jsx("div", { className: "muted small", children: "Share survey" }) }),
      /* @__PURE__ */ jsx("p", { className: "muted small", children: "Generate a link or QR code to collect responses. Add optional location details to tag responses for analytics." }),
      /* @__PURE__ */ jsx("button", { type: "button", className: "btn primary", onClick: () => setShareOpen(true), children: "Share survey" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "card builder-form-card", children: [
      /* @__PURE__ */ jsxs("div", { className: "field", children: [
        /* @__PURE__ */ jsx("span", { children: "Survey title" }),
        /* @__PURE__ */ jsx("p", { className: "builder-survey-title-readonly", children: title }),
        /* @__PURE__ */ jsx("p", { className: "muted small", style: { marginTop: "0.35rem" }, children: "Set when the survey was created. Not editable here." })
      ] }),
      /* @__PURE__ */ jsxs("label", { className: "field", style: { marginTop: "0.9rem" }, children: [
        /* @__PURE__ */ jsx("span", { children: "Description" }),
        /* @__PURE__ */ jsx(
          "textarea",
          {
            rows: 3,
            value: description,
            placeholder: "Optional — shown to respondents under the survey title.",
            onChange: (e) => setDescription(e.target.value)
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "card builder-media-card", children: [
      /* @__PURE__ */ jsxs("div", { className: "builder-media-block", children: [
        /* @__PURE__ */ jsx("span", { className: "builder-media-label", children: "Banner image" }),
        /* @__PURE__ */ jsx("p", { className: "muted small builder-media-hint", children: "A wide banner shown above the survey title. Upload any image and drag it in the frame to choose what shows." }),
        /* @__PURE__ */ jsx(
          SurveyBannerEditor,
          {
            surveyId: id,
            bannerImage,
            bannerPosition,
            onChange: (next) => {
              setBannerImage(next.banner_image ?? null);
              setBannerPosition(next.banner_position ?? null);
            }
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "builder-media-block", children: [
        /* @__PURE__ */ jsx("span", { className: "builder-media-label", children: "Client logo" }),
        /* @__PURE__ */ jsxs("p", { className: "muted small builder-media-hint", children: [
          "Set once per client on the ",
          /* @__PURE__ */ jsx(Link, { to: "/clients", children: "Clients page" }),
          " — every survey for that client uses it automatically."
        ] }),
        logoImage
          ? /* @__PURE__ */ jsx("img", { src: logoImage, alt: "Client logo", className: "logo-frame-img builder-logo-preview" })
          : /* @__PURE__ */ jsx("p", { className: "muted small", children: "No logo set for this client yet." })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "card builder-respondent-info-card", children: /* @__PURE__ */ jsxs("label", { className: "builder-respondent-info-label", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          checked: collectRespondentInfo,
          onChange: (e) => setCollectRespondentInfo(e.target.checked)
        }
      ),
      /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("strong", { children: "Collect respondent name and email" }),
        /* @__PURE__ */ jsxs("span", { className: "muted small builder-respondent-info-hint", children: [
          "Adds two short-text questions at the ",
          /* @__PURE__ */ jsx("strong", { children: "start" }),
          " of the survey (name and email). You can edit the wording and whether each is required. Turn this off to remove them."
        ] })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: "card builder-respondent-info-card", children: /* @__PURE__ */ jsxs("label", { className: "builder-respondent-info-label", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          checked: showQuestionNumbers,
          onChange: (e) => setShowQuestionNumbers(e.target.checked)
        }
      ),
      /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("strong", { children: "Show question numbers" }),
        /* @__PURE__ */ jsx("span", { className: "muted small builder-respondent-info-hint", children: "Numbers questions 1, 2, 3... continuously across every section." })
      ] })
    ] }) }),
    /* @__PURE__ */ jsxs("div", { className: "card builder-sections-card", children: [
      /* @__PURE__ */ jsx("span", { className: "builder-media-label", children: "Survey sections (optional)" }),
      /* @__PURE__ */ jsx("p", { className: "muted small builder-media-hint", children: "Split the survey into numbered tabs respondents step through with Next/Back — like “1. Account Info”, “2. BMS Details”. Leave empty for a single-page survey." }),
      sections.length === 0 ? /* @__PURE__ */ jsx("p", { className: "muted small", children: "No sections yet — this survey renders as one page." }) : /* @__PURE__ */ jsx("div", { className: "builder-sections-list", children: sections.map((sec, i) => /* @__PURE__ */ jsxs("div", { className: "builder-section-row", children: [
        /* @__PURE__ */ jsx("span", { className: "builder-section-index", "aria-hidden": "true", children: i + 1 }),
        /* @__PURE__ */ jsxs("div", { className: "builder-section-fields", children: [
          /* @__PURE__ */ jsxs("label", { className: "field", children: [
            /* @__PURE__ */ jsx("span", { children: "Tab name" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                value: sec.tab_label,
                onChange: (e) => patchSection(i, { tab_label: e.target.value }),
                placeholder: "e.g. BMS Details",
                maxLength: 80
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("label", { className: "field", children: [
            /* @__PURE__ */ jsx("span", { children: "Detailed heading (shown inside the section)" }),
            /* @__PURE__ */ jsx(
              "input",
              {
                value: sec.title,
                onChange: (e) => patchSection(i, { title: e.target.value }),
                placeholder: "e.g. BMS Infrastructure Details",
                maxLength: 300
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "builder-section-actions", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "btn small-btn",
              onClick: () => moveSection(i, -1),
              disabled: i === 0,
              title: "Move section up",
              "aria-label": "Move section up",
              children: "↑"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "btn small-btn",
              onClick: () => moveSection(i, 1),
              disabled: i === sections.length - 1,
              title: "Move section down",
              "aria-label": "Move section down",
              children: "↓"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "btn small-btn danger",
              onClick: () => removeSection(i),
              children: "Remove"
            }
          )
        ] })
      ] }, i)) }),
      /* @__PURE__ */ jsx("button", { type: "button", className: "btn secondary", onClick: addSection, children: "+ Add section" })
    ] }),
    /* Actions sit ABOVE the question list so they stay reachable without
       scrolling past every question. */
    /* @__PURE__ */ jsxs("div", { className: "builder-actions-bar", children: [
      /* @__PURE__ */ jsx("button", { type: "button", className: "btn secondary stretch builder-add-q", style: { flex: "1 1 12rem" }, onClick: addQuestion, children: "+ Add question" }),
      /* @__PURE__ */ jsx("button", { type: "button", className: "btn secondary stretch", style: { flex: "1 1 12rem" }, onClick: () => setTemplatePickerOpen(true), children: "⧉ Add from templates" }),
      canCurateGroups && /* @__PURE__ */ jsx("button", { type: "button", className: "btn secondary stretch", style: { flex: "1 1 12rem" }, onClick: () => setNewGroupOpen(true), children: "+ New question group" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "builder-questions", children: [
      questions.length === 0 && /* @__PURE__ */ jsxs("div", { className: "builder-empty-state", children: [
        /* @__PURE__ */ jsx("p", { className: "builder-empty-title", children: "No questions yet" }),
        /* @__PURE__ */ jsxs("p", { className: "builder-empty-hint", children: [
          "Enable ",
          /* @__PURE__ */ jsx("strong", { children: "Collect respondent name and email" }),
          " above, or click ",
          /* @__PURE__ */ jsx("strong", { children: "+ Add question" }),
          " ",
          "above to start building your survey."
        ] })
      ] }),
      questions.map((q, idx) => (
        // Task 3 — has-error class; Task 4 — padding-left for grip
        /* @__PURE__ */ jsxs("div", {
          className: `card builder-q-card${labelErrors.has(idx) ? " has-error" : ""}${dragIdx === idx ? " is-dragging" : ""}${dragIdx != null && dragIdx !== idx && dragOverIdx === idx ? idx > dragIdx ? " drag-over-below" : " drag-over-above" : ""}`,
          draggable: armedIdx === idx,
          onDragStart: (e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(idx));
            setDragIdx(idx);
          },
          onDragOver: (e) => {
            if (dragIdx == null || dragIdx === idx) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (dragOverIdx !== idx) setDragOverIdx(idx);
          },
          onDrop: (e) => {
            if (dragIdx == null) return;
            e.preventDefault();
            reorderQuestion(dragIdx, idx);
            endDrag();
          },
          onDragEnd: endDrag,
          children: [
          /* @__PURE__ */ jsx("div", {
            className: `builder-q-grip${isRespondentProfileQuestion(q) ? " is-locked" : ""}`,
            title: isRespondentProfileQuestion(q) ? "Respondent details stay at the top" : "Drag to reorder",
            "aria-hidden": "true",
            onMouseDown: () => {
              if (!isRespondentProfileQuestion(q)) setArmedIdx(idx);
            },
            children: "\u283F"
          }),
          /* @__PURE__ */ jsxs("div", { className: "builder-q-head", children: [
            /* @__PURE__ */ jsx("span", { className: "badge ghost", children: isRespondentProfileQuestion(q) ? "Respondent details" : `Question ${idx + 1}` }),
            /* @__PURE__ */ jsxs("div", { className: "builder-q-actions", children: [
              q.type === "single" && !isRespondentProfileQuestion(q) && /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: `btn small-btn${branchRuleCount(q) > 0 ? " builder-branch-btn-active" : ""}`,
                  onClick: () => toggleBranchingOpen(q.id),
                  title: "Show extra questions when a specific answer is selected",
                  children: branchRuleCount(q) > 0 ? `Display logic (${branchRuleCount(q)})` : "Display logic"
                }
              ),
              CUSTOMIZABLE_TYPES.has(q.type) && /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "btn small-btn",
                  onClick: () => setCustomizeIdx(idx),
                  title: "Customize how this question is shown to respondents",
                  children: "Customize"
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "btn small-btn",
                  onClick: () => move(idx, -1),
                  disabled: idx === 0 || isRespondentProfileQuestion(q),
                  title: "Move up",
                  "aria-label": "Move question up",
                  children: "\u2191"
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "btn small-btn",
                  onClick: () => move(idx, 1),
                  disabled: idx === questions.length - 1 || isRespondentProfileQuestion(q),
                  title: "Move down",
                  "aria-label": "Move question down",
                  children: "\u2193"
                }
              ),
              !isRespondentProfileQuestion(q) && /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "btn small-btn builder-insert-btn",
                  onClick: () => addQuestionBelow(idx),
                  title: "Add question below",
                  "aria-label": "Add question below",
                  children: "+"
                }
              ),
              isRespondentProfileQuestion(q) ? /* @__PURE__ */ jsx("span", { className: "muted small builder-profile-managed", children: "Unset the option above to remove." }) : confirmRemove === idx ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("span", { className: "builder-confirm-label", children: "Remove?" }),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "btn small-btn danger",
                    onClick: () => removeQuestion(idx),
                    children: "Yes, remove"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "btn small-btn",
                    onClick: () => setConfirmRemove(null),
                    children: "Keep"
                  }
                )
              ] }) : /* @__PURE__ */ jsx("button", { type: "button", className: "btn small-btn danger", onClick: () => requestRemove(idx), children: "Remove" })
            ] })
          ] }),
          labelErrors.has(idx) && /* @__PURE__ */ jsx("p", { className: "builder-q-error", children: "Question text is required." }),
          /* @__PURE__ */ jsxs("div", { className: "grid builder-q-grid", children: [
            /* @__PURE__ */ jsxs("label", { className: "field", children: [
              /* @__PURE__ */ jsx("span", { children: "Type" }),
              isRespondentProfileQuestion(q) ? /* @__PURE__ */ jsx("select", { value: q.type, disabled: true, className: "builder-q-type-locked", children: /* @__PURE__ */ jsx("option", { value: "text", children: "Short text (fixed)" }) }) : /* @__PURE__ */ jsx(
                "select",
                {
                  value: q.type,
                  onChange: (e) => patchQuestion(idx, { type: e.target.value }),
                  children: TYPE_LABELS.map((t) => /* @__PURE__ */ jsx("option", { value: t.value, children: t.label }, t.value))
                }
              )
            ] }),
            sections.length > 0 && !isRespondentProfileQuestion(q) && /* @__PURE__ */ jsxs("label", { className: "field", children: [
              /* @__PURE__ */ jsx("span", { children: "Section" }),
              /* @__PURE__ */ jsxs(
                "select",
                {
                  value: q.section_index ?? "",
                  onChange: (e) => setQuestionSection(idx, e.target.value),
                  children: [
                    /* @__PURE__ */ jsx("option", { value: "", children: "Unassigned" }),
                    sections.map((s, si) => /* @__PURE__ */ jsxs("option", { value: si, children: [si + 1, ". ", s.tab_label || `Section ${si + 1}`] }, si))
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs("label", { className: "field builder-label-full", children: [
              /* @__PURE__ */ jsx("span", { children: "Question text" }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  value: q.label,
                  onChange: (e) => patchQuestion(idx, { label: e.target.value }),
                  placeholder: "What do you want to ask?"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs("label", { className: "field builder-required", children: [
              /* @__PURE__ */ jsx("span", { children: "Required" }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  type: "checkbox",
                  checked: q.required,
                  onChange: (e) => patchQuestion(idx, { required: e.target.checked })
                }
              )
            ] })
          ] }),
          CUSTOMIZABLE_TYPES.has(q.type) && /* @__PURE__ */ jsx("div", { className: "builder-q-config-summary muted small", children: questionConfigSummary(q.type, q.config) || /* @__PURE__ */ jsxs("span", { children: [
            "Default settings.",
            " ",
            /* @__PURE__ */ jsx("button", { type: "button", className: "link-inline", onClick: () => setCustomizeIdx(idx), children: "Customize" })
          ] }) }),
          needsOptions(q.type) && /* @__PURE__ */ jsxs("div", { className: "builder-options", children: [
            /* @__PURE__ */ jsx("div", { className: "muted small", children: q.type === "likert" ? "Statements (each is rated on the shared scale)" : "Answer choices" }),
            q.type === "emotion" ? /* @__PURE__ */ jsxs("div", { className: "builder-emotion-pills", children: [
              q.options.map((o) => /* @__PURE__ */ jsx("span", { className: "builder-emotion-pill", children: o.label }, o.key)),
              /* @__PURE__ */ jsx("span", { className: "builder-emotion-fixed-note", children: "Fixed options" })
            ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
              q.options.map((o, oi) => /* @__PURE__ */ jsxs("div", { className: `builder-option-row${q.type === "single" && branchingOpen.has(q.id) ? " has-branching" : ""}`, children: [
                /* @__PURE__ */ jsxs("div", { className: "builder-option-fields", children: [
                  /* @__PURE__ */ jsx(
                    "input",
                    {
                      value: o.label,
                      onChange: (e) => patchOption(idx, oi, e.target.value),
                      placeholder: q.type === "likert" ? "Statement" : "Option label"
                    }
                  ),
                  (q.type === "single" || q.type === "multi") && /* @__PURE__ */ jsx(
                    "input",
                    {
                      className: "builder-option-description-input",
                      value: o.description || "",
                      onChange: (e) => patchOptionDescription(idx, oi, e.target.value),
                      placeholder: "Optional description shown under this option",
                      maxLength: 500
                    }
                  ),
                  (q.type === "single" || q.type === "multi") && /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: `btn small-btn builder-option-other-btn${o.is_other ? " is-on" : ""}`,
                      onClick: () => patchOptionIsOther(idx, oi, !o.is_other),
                      title: o.is_other ? "This is the “Other” option: respondents can type their own answer. Click to unset." : "Make this the “Other” option: respondents who pick it can type their own answer",
                      children: o.is_other ? "✎ Other on" : "✎ Other"
                    }
                  )
                ] }),
                q.type === "single" && branchingOpen.has(q.id) && branchShowPanel(idx, o),
                /* @__PURE__ */ jsx("button", { type: "button", className: "btn small-btn", onClick: () => removeOption(idx, oi), children: "\xD7" })
              ] }, o.key + oi)),
              /* @__PURE__ */ jsx("button", { type: "button", className: "btn small-btn", onClick: () => addOption(idx), children: q.type === "likert" ? "+ Add statement" : "+ Add option" })
            ] })
          ] }),
          q.type === "single" && branchRuleCount(q) > 0 && !branchingOpen.has(q.id) && /* @__PURE__ */ jsxs("div", { className: "builder-q-config-summary muted small", children: [
            `Display logic: ${branchRuleCount(q)} option${branchRuleCount(q) === 1 ? "" : "s"} reveal${branchRuleCount(q) === 1 ? "s" : ""} extra questions.`,
            " ",
            /* @__PURE__ */ jsx("button", { type: "button", className: "link-inline", onClick: () => toggleBranchingOpen(q.id), children: "Edit" })
          ] }),

        ] }, q.id)
      ))
    ] }),
    /* @__PURE__ */ jsx(
      QuestionTemplatePicker,
      {
        open: templatePickerOpen,
        onClose: () => setTemplatePickerOpen(false),
        onApply: applyTemplateGroups,
        existingDefinitionIds: questions.map((q) => q.question_definition_id).filter(Boolean)
      },
      templatePickerKey
    ),
    /* @__PURE__ */ jsx(
      QuestionGroupEditorDialog,
      {
        open: newGroupOpen,
        mode: "create",
        onClose: () => setNewGroupOpen(false),
        // Newly created group is immediately available in the template picker.
        onSaved: () => {
          setTemplatePickerKey((k) => k + 1);
          setShowDraftToast(false);
        }
      }
    ),
    /* @__PURE__ */ jsx(
      AddQuestionGroupPrompt,
      {
        open: groupPromptOpen,
        usedGroupIds,
        onChoose: addQuestionWithGroup,
        onClose: () => setGroupPromptOpen(false)
      }
    ),
    customizeIdx !== null && questions[customizeIdx] && /* @__PURE__ */ jsx(
      CustomizeQuestionModal,
      {
        q: questions[customizeIdx],
        onClose: () => setCustomizeIdx(null),
        onChangeConfig: (next) => setQuestionConfig(customizeIdx, next),
        onApplyEmotionPreset: (presetKey) => {
          const preset = EMOTION_PRESETS[presetKey];
          if (!preset) return;
          setQuestionOptions(
            customizeIdx,
            preset.options.map((o) => ({ key: o.key, label: o.label }))
          );
          const cur = questions[customizeIdx]?.config ?? {};
          setQuestionConfig(customizeIdx, { ...cur, preset: presetKey });
        }
      }
    ),
    publishOpen && /* @__PURE__ */ jsx(
      "div",
      {
        className: "modal-backdrop modal-backdrop--publish",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "publish-modal-title",
        onMouseDown: (e) => {
          if (e.target === e.currentTarget && !publishing) setPublishOpen(false);
        },
        children: /* @__PURE__ */ jsxs("div", { className: "modal-card card modal-card--publish", onMouseDown: (e) => e.stopPropagation(), children: [
          /* @__PURE__ */ jsx("h2", { id: "publish-modal-title", children: "Publish survey" }),
          /* @__PURE__ */ jsx("p", { className: "muted small publish-modal-lede", children: "Check the respondent view, set the live window, then confirm publish. Times are shown in your local time." }),
          /* @__PURE__ */ jsxs("form", { onSubmit: (e) => void submitPublish(e), children: [
            /* @__PURE__ */ jsxs("div", { className: "publish-modal-schedule", children: [
              /* @__PURE__ */ jsxs("div", { className: "schedule-mode-toggle", role: "radiogroup", "aria-label": "Schedule type", children: [
                /* @__PURE__ */ jsxs("label", { className: "schedule-mode-option", children: [
                  /* @__PURE__ */ jsx("input", { type: "radio", name: "publishMode", value: "once", checked: publishMode === "once", onChange: () => setPublishMode("once") }),
                  "One-time window"
                ] }),
                /* @__PURE__ */ jsxs("label", { className: "schedule-mode-option", children: [
                  /* @__PURE__ */ jsx("input", { type: "radio", name: "publishMode", value: "recurring", checked: publishMode === "recurring", onChange: () => setPublishMode("recurring") }),
                  "Recurring"
                ] })
              ] }),
              publishMode === "once" && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("h3", { className: "publish-modal-section", children: "Live window" }),
                /* @__PURE__ */ jsxs("label", { className: "field", children: [
                  /* @__PURE__ */ jsx("span", { children: "Opens at (local time)" }),
                  /* @__PURE__ */ jsx("input", { type: "datetime-local", required: publishMode === "once", value: opensAt, onChange: (e) => setOpensAt(e.target.value) }),
                  /* @__PURE__ */ jsxs("span", { className: "muted small publish-modal-utc", children: [
                    "UTC: ",
                    formatUtcPreview(opensAt)
                  ] })
                ] }),
                /* @__PURE__ */ jsxs("label", { className: "field", children: [
                  /* @__PURE__ */ jsx("span", { children: "Closes at (local time)" }),
                  /* @__PURE__ */ jsx("input", { type: "datetime-local", required: publishMode === "once", value: closesAt, onChange: (e) => setClosesAt(e.target.value) }),
                  /* @__PURE__ */ jsxs("span", { className: "muted small publish-modal-utc", children: [
                    "UTC: ",
                    formatUtcPreview(closesAt)
                  ] })
                ] })
              ] }),
              publishMode === "recurring" && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("h3", { className: "publish-modal-section", children: "Recurring schedule" }),
                /* @__PURE__ */ jsxs("label", { className: "field", children: [
                  /* @__PURE__ */ jsx("span", { children: "Repeats" }),
                  /* @__PURE__ */ jsxs("select", { value: recurrenceFrequency, onChange: (e) => setRecurrenceFrequency(e.target.value), children: [
                    /* @__PURE__ */ jsx("option", { value: "daily", children: "Daily" }),
                    /* @__PURE__ */ jsx("option", { value: "weekly", children: "Weekly" }),
                    /* @__PURE__ */ jsx("option", { value: "monthly", children: "Monthly" }),
                    /* @__PURE__ */ jsx("option", { value: "quarterly", children: "Quarterly" }),
                    /* @__PURE__ */ jsx("option", { value: "yearly", children: "Yearly" })
                  ] })
                ] }),
                recurrenceFrequency !== "daily" && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsxs("label", { className: "field", children: [
                    /* @__PURE__ */ jsx("span", { children: "Open for (days per cycle)" }),
                    /* @__PURE__ */ jsx("input", { type: "number", min: 1, max: 366, required: publishMode === "recurring" && recurrenceFrequency !== "daily", value: windowDurationDays, onChange: (e) => setWindowDurationDays(Number(e.target.value)) })
                  ] }),
                  /* @__PURE__ */ jsxs("label", { className: "publish-modal-notify-toggle", children: [
                    /* @__PURE__ */ jsx("input", { type: "checkbox", checked: !excludeWeekends, onChange: (e) => setExcludeWeekends(!e.target.checked) }),
                    "Count weekends"
                  ] }),
                  excludeWeekends && /* @__PURE__ */ jsx("p", { className: "muted small", children: "Saturdays and Sundays don't count toward the days open — the close date moves out past any weekends in the window." })
                ] }),
                recurrenceFrequency === "daily" && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsxs("label", { className: "field", children: [
                    /* @__PURE__ */ jsx("span", { children: "Opens daily at (local time)" }),
                    /* @__PURE__ */ jsx("input", { type: "time", required: true, value: dailyOpenTime, onChange: (e) => setDailyOpenTime(e.target.value) }),
                    /* @__PURE__ */ jsxs("span", { className: "muted small publish-modal-utc", children: [
                      "UTC: ",
                      localTimeToUtcHHMM(dailyOpenTime) || "—"
                    ] })
                  ] }),
                  /* @__PURE__ */ jsxs("label", { className: "field", children: [
                    /* @__PURE__ */ jsx("span", { children: "Closes daily at (local time)" }),
                    /* @__PURE__ */ jsx("input", { type: "time", required: true, value: dailyCloseTime, onChange: (e) => setDailyCloseTime(e.target.value) }),
                    /* @__PURE__ */ jsxs("span", { className: "muted small publish-modal-utc", children: [
                      "UTC: ",
                      localTimeToUtcHHMM(dailyCloseTime) || "—"
                    ] })
                  ] }),
                  dailyOpenTime && dailyCloseTime && dailyOpenTime === dailyCloseTime && /* @__PURE__ */ jsx("span", { className: "field-error", children: "Open and close times must be different." }),
                  dailyOpenTime && dailyCloseTime && dailyCloseTime < dailyOpenTime && /* @__PURE__ */ jsx("p", { className: "muted small", children: "Close time is before open time — the survey stays open overnight into the next day." })
                ] }),
                /* @__PURE__ */ jsxs("label", { className: "field", children: [
                  /* @__PURE__ */ jsx("span", { children: "Series starts (local time)" }),
                  /* @__PURE__ */ jsx("input", { type: "datetime-local", required: publishMode === "recurring", value: opensAt, onChange: (e) => setOpensAt(e.target.value) }),
                  /* @__PURE__ */ jsxs("span", { className: "muted small publish-modal-utc", children: [
                    "UTC: ",
                    formatUtcPreview(opensAt)
                  ] })
                ] }),
                /* @__PURE__ */ jsxs("label", { className: "field", children: [
                  /* @__PURE__ */ jsx("span", { children: "Series ends (local time, optional)" }),
                  /* @__PURE__ */ jsx("input", { type: "datetime-local", value: seriesEndsAt, onChange: (e) => setSeriesEndsAt(e.target.value) }),
                  seriesEndsAt ? /* @__PURE__ */ jsxs("span", { className: "muted small publish-modal-utc", children: [
                    "UTC: ",
                    formatUtcPreview(seriesEndsAt)
                  ] }) : /* @__PURE__ */ jsx("span", { className: "muted small publish-modal-utc", children: "Leave empty to repeat until paused." }),
                  seriesEndsAt && opensAt && new Date(seriesEndsAt) <= new Date(opensAt) && /* @__PURE__ */ jsx("span", { className: "field-error", children: "Series end must be after the series start." })
                ] })
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "publish-modal-notify-toggle", children: [
                /* @__PURE__ */ jsx("input", { type: "checkbox", checked: notifyEnabled, onChange: (e) => setNotifyEnabled(e.target.checked) }),
                "Notify recipients"
              ] }),
              notifyEnabled ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("p", { className: "muted small", children: "A KPI summary email is sent to these addresses when the survey closes (manually or on schedule)." }),
                /* @__PURE__ */ jsxs("label", { className: "field", children: [
                  /* @__PURE__ */ jsx("span", { children: "To" }),
                  /* @__PURE__ */ jsx("input", { type: "email", required: true, value: notifyToEmail, onChange: (e) => setNotifyToEmail(e.target.value), "aria-invalid": notifyToEmail.trim().length > 0 && !notifyToValid }),
                  notifyToEmail.trim().length > 0 && !notifyToValid && /* @__PURE__ */ jsx("span", { className: "field-error", children: "Enter a valid email address." })
                ] }),
                /* @__PURE__ */ jsxs("label", { className: "field", children: [
                  /* @__PURE__ */ jsx("span", { children: "CC (comma-separated, optional)" }),
                  /* @__PURE__ */ jsx("input", { type: "text", value: notifyCcEmailsRaw, onChange: (e) => setNotifyCcEmailsRaw(e.target.value), placeholder: "name@jll.com, name2@jll.com", "aria-invalid": notifyCcEmailsRaw.trim().length > 0 && !notifyCcValid }),
                  notifyCcEmailsRaw.trim().length > 0 && !notifyCcValid && /* @__PURE__ */ jsx("span", { className: "field-error", children: "Enter valid CC email address(es)." })
                ] })
              ] }) : /* @__PURE__ */ jsx("p", { className: "muted small", children: "The close-out summary email goes to you by default. Tick the box to send it to other people." })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "publish-modal-preview-host", children: [
              /* @__PURE__ */ jsx("h3", { className: "publish-modal-section", children: "Preview" }),
              publishPreviewLoading && /* @__PURE__ */ jsx("p", { className: "muted small", children: "Loading preview\u2026" }),
              !publishPreviewLoading && !publishPreviewErr && publishPreviewSurvey && /* @__PURE__ */ jsxs("div", { className: "publish-modal-preview preview-card", children: [
                publishPreviewSurvey.banner_image && /* @__PURE__ */ jsx("div", { className: "public-survey-banner", children: /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: publishPreviewSurvey.banner_image,
                    alt: "",
                    style: {
                      objectPosition: `${publishPreviewSurvey.banner_position?.x ?? 50}% ${publishPreviewSurvey.banner_position?.y ?? 50}%`
                    }
                  }
                ) }),
                publishPreviewSurvey.logo_image && /* @__PURE__ */ jsx("img", { src: publishPreviewSurvey.logo_image, alt: "", className: "public-survey-logo" }),
                publishPreviewSurvey.description?.trim() && /* @__PURE__ */ jsx("p", { className: "public-survey-lede", children: publishPreviewSurvey.description }),
                /* @__PURE__ */ jsx(
                  SurveyFields,
                  {
                    survey: publishPreviewSurvey,
                    answers: previewAnswers,
                    setAnswers: setPreviewAnswers,
                    readOnly: true
                  }
                )
              ] })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "builder-modal-actions publish-modal-actions", children: [
              /* @__PURE__ */ jsx("button", { type: "button", className: "btn", disabled: publishing, onClick: () => setPublishOpen(false), children: "Cancel" }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "submit",
                  className: "btn primary",
                  disabled: publishing || !opensAt || (publishMode === "once" && !closesAt.trim()) || (publishMode === "recurring" && recurrenceFrequency === "daily" && (!dailyOpenTime || !dailyCloseTime || dailyOpenTime === dailyCloseTime)) || (publishMode === "recurring" && !!seriesEndsAt && new Date(seriesEndsAt) <= new Date(opensAt)) || !notifyValid || publishPreviewLoading || !!publishPreviewErr || !publishPreviewSurvey,
                  children: publishing ? "Publishing\u2026" : publishMode === "recurring" ? "Start recurring" : "Publish now"
                }
              )
            ] })
          ] })
        ] })
      }
    ),
    publishSuccessOpen && publicUrl && /* @__PURE__ */ jsx(
      "div",
      {
        className: "modal-backdrop modal-backdrop--publish-success",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "publish-success-title",
        onMouseDown: (e) => {
          if (e.target === e.currentTarget) setPublishSuccessOpen(false);
        },
        children: /* @__PURE__ */ jsxs("div", { className: "modal-card card modal-card--publish-success", onMouseDown: (e) => e.stopPropagation(), children: [
          /* @__PURE__ */ jsx("h2", { id: "publish-success-title", children: "Survey is live" }),
          /* @__PURE__ */ jsx("p", { className: "muted small publish-success-lede", children: "Generate a link or QR code to start collecting responses. Location details are optional." }),
          /* @__PURE__ */ jsxs("div", { className: "builder-modal-actions publish-success-actions", children: [
            /* @__PURE__ */ jsx("button", { type: "button", className: "btn", onClick: () => setPublishSuccessOpen(false), children: "Close" }),
            /* @__PURE__ */ jsx("button", { type: "button", className: "btn primary", onClick: () => {
              setPublishSuccessOpen(false);
              setShareOpen(true);
            }, children: "Share survey" })
          ] })
        ] })
      }
    ),
    confirmEndLiveOpen && /* @__PURE__ */ jsx(
      "div",
      {
        className: "modal-backdrop modal-backdrop--confirm modal-backdrop--confirm-builder",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "end-live-title",
        onMouseDown: (e) => {
          if (e.target === e.currentTarget) setConfirmEndLiveOpen(false);
        },
        children: /* @__PURE__ */ jsxs("div", { className: "modal-card card modal-card--confirm", onMouseDown: (e) => e.stopPropagation(), children: [
          /* @__PURE__ */ jsx("h2", { id: "end-live-title", children: "End live survey?" }),
          /* @__PURE__ */ jsx("p", { className: "muted small", children: "This will stop accepting responses at the public link. You can publish again later to reopen it." }),
          /* @__PURE__ */ jsxs("div", { className: "builder-modal-actions", children: [
            /* @__PURE__ */ jsx("button", { type: "button", className: "btn", onClick: () => setConfirmEndLiveOpen(false), children: "Keep live" }),
            /* @__PURE__ */ jsx("button", { type: "button", className: "btn danger", onClick: () => void endLive(), children: "End live" })
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(SurveyShareDialog, {
      open: shareOpen,
      onClose: () => setShareOpen(false),
      surveyId: id,
      surveyTitle: title,
      isLiveWindowOpen: liveOpen,
      liveWindowState,
      onCopySuccess: () => setCopyToastKey(Date.now()),
      onCopyError: () => showError("Failed to copy link to clipboard.")
    })
  ] });
}
export default SurveyBuilderPage;
